import { EMobileGenerationType } from "@kuika/kuika-cl-model";
import { ReactNode } from "react";
import { KIcon } from "../../../../../shared/icon/k-icon";
import KuikaRadioGroup, { IKuikaRadioOption } from "../../../../ui-kit/kuika-radio-group/kuika-radio-group";
import { isReactNativeGenerationOffered } from "../../react-native-feature-gate";
import { ConfigField, ConfigSection } from "../config-pane-parts";

export type GenerationTypePlatform = "android" | "ios";

interface IGenerationTypePlatformCopy {
  /** Native option's leading glyph — the platform's own emblem. */
  nativeIcon: JSX.Element;
  /** What the Native option produces for this platform. */
  nativeDescription: string;
  /** What the React Native option produces, naming the platform it shares the Expo project with. */
  reactNativeDescription: string;
  /**
   * The section heading — the section is owned by this control so it can vanish with it. Title Case like
   * every other `ConfigSection` title; the heading uppercases in CSS.
   */
  sectionTitle: string;
}

const PLATFORM_COPY: Record<GenerationTypePlatform, IGenerationTypePlatformCopy> = {
  android: {
    nativeIcon: <KIcon iconLibrary="fontawesome" iconType="brands" iconName="android" />,
    nativeDescription: "Android (Kotlin) project",
    reactNativeDescription: "Expo project, shared with iOS",
    sectionTitle: "Android Generation Type"
  },
  ios: {
    nativeIcon: <KIcon iconLibrary="fontawesome" iconType="brands" iconName="apple" />,
    nativeDescription: "iOS (Swift) project",
    reactNativeDescription: "Expo project, shared with Android",
    sectionTitle: "iOS Generation Type"
  }
};

const buildOptions = (platform: GenerationTypePlatform): IKuikaRadioOption[] => {
  const copy: IGenerationTypePlatformCopy = PLATFORM_COPY[platform];
  return [
    {
      label: "Native",
      value: EMobileGenerationType.Native,
      description: copy.nativeDescription,
      icon: copy.nativeIcon
    },
    {
      label: "React Native",
      value: EMobileGenerationType.ReactNative,
      description: copy.reactNativeDescription,
      badge: "Beta",
      badgeTone: "warning",
      icon: <KIcon iconLibrary="fontawesome" iconType="brands" iconName="react" />
    }
  ];
};

export interface IConfigGenerationTypeInputProps {
  /** Which platform tab this control belongs to — decides the copy and the Native glyph. */
  platform: GenerationTypePlatform;
  /** Injected by the surrounding antd `Form.Item name="androidGenerationType" | "iosGenerationType"`. */
  value?: EMobileGenerationType;
  onChange?: (value: EMobileGenerationType) => void;
  /**
   * Whether the React Native option may be picked here. Defaults to the RN_BETA_ALLOWLIST gate
   * (`isReactNativeGenerationOffered`); tests pin it explicitly.
   */
  reactNativeOffered?: boolean;
  /**
   * The section's explanatory paragraph, resolved by the pane (help content with a hard-coded fallback). The
   * whole section — heading, paragraph and control — is rendered here so that outside the closed beta it
   * disappears as one unit instead of leaving a heading over an empty space. The pane hosts it in its aside
   * column, beside the platform's support switch: it is a platform-wide decision, not one category's row.
   */
  sectionDescription?: ReactNode;
}

export const ConfigGenerationTypeInput = ({
  platform,
  value,
  onChange,
  reactNativeOffered,
  sectionDescription
}: IConfigGenerationTypeInputProps): JSX.Element | null => {
  // Not offered (React Native on hold, or outside the closed beta) there is nothing to choose: CoreApi generates
  // every mobile platform of this caller as Native whatever the configuration holds, so the whole
  // SECTION (heading, paragraph, control) disappears — also when the configuration already says React Native.
  // That saved value is left alone on purpose: it is what lets reopening the gate bring React Native back. The
  // tabs keep the surrounding `Form.Item` mounted — save rebuilds the configuration from the mounted fields.
  const isReactNativeOffered: boolean = reactNativeOffered ?? isReactNativeGenerationOffered();
  if (!isReactNativeOffered) {
    return null;
  }

  // Configurations saved before this setting existed carry no value; they generate
  // native Android/iOS projects, so that is what the control has to show.
  const selectedValue: EMobileGenerationType = value ?? EMobileGenerationType.Native;
  const copy: IGenerationTypePlatformCopy = PLATFORM_COPY[platform];
  const options: IKuikaRadioOption[] = buildOptions(platform);

  const handleChange = (nextValue: string | number): void => {
    if (!onChange) {
      return;
    }
    const nextGenerationType: EMobileGenerationType =
      Number(nextValue) === EMobileGenerationType.ReactNative
        ? EMobileGenerationType.ReactNative
        : EMobileGenerationType.Native;
    onChange(nextGenerationType);
  };

  return (
    <ConfigSection
      rail
      title={copy.sectionTitle}
      titleTestId={`${platform}GenerationTypeTitle`}
      description={sectionDescription}
      descriptionTestId={`${platform}GenerationTypeDescription`}
    >
      {/* Explicit path: this control is the surrounding `Form.Item`'s child, not its parent, so
          `readFieldPath`'s discovery walk would find nothing to register for the changed mark. */}
      <ConfigField
        aside
        label="Generation Type"
        tooltipContentKey="appsettings_mobilegenerationtype_generation_type"
        formInputName={[`${platform}GenerationType`]}
      >
        {/* One column: the aside is a 300px rail, and a two-column card grid there truncates the
            option descriptions. */}
        <KuikaRadioGroup variant="card" columns={1} options={options} value={selectedValue} onChange={handleChange} />
      </ConfigField>
    </ConfigSection>
  );
};
